import { useEffect, useState } from "react";
import i18n from "../../language/i18n";
import { Icon } from "../../library";
import { useAuth } from "../../context/UserContextProvider";
import { endOwnImpersonation } from "../../requests/ManagementRequests";
import { retrieveAdminAppUrl } from "../../utilization/ImpersonationHandoffUtilization";
import {
  clearImpersonationState,
  IMPERSONATION_STATE_EVENT,
  isImpersonatingBySession,
  readImpersonationState,
} from "../../utilization/ImpersonationSessionUtilization";
import { impersonationBannerStyles } from "./impersonationBannerStyles";

/**
 * The "you are viewing as X" band, receive-side.
 *
 * <p>Non-dismissible, and that is the design. Impersonation is a SUPPORT tool, not a covert one: an admin
 * must never be able to forget they are acting as someone else, and a learner walking past their own screen
 * must be able to see it. No close button, no auto-hide.
 *
 * <p>Simpler than admin-web's twin because app-web is always the CROSS-ORIGIN target: the admin's own
 * admin-web session lives on a different origin and was never touched, so returning is a plain navigation
 * back to a session that is still signed in. There is no actor stash here to restore.
 */
const ImpersonationBanner = () => {
  const { handleStateLogout } = useAuth();
  const [state, setState] = useState(() => readImpersonationState());
  const [returning, setReturning] = useState(false);

  // Another tab on this origin can end the session; keep in step rather than showing a banner for a session
  // that is over.
  // BOTH events, and the same-tab one is the load-bearing half. `storage` fires only in OTHER tabs — never
  // in the one that wrote — and the tab that redeems the handoff code is exactly that tab. It finishes with
  // an SPA navigation, not a reload, so this component never remounted and the state captured at mount
  // (taken before the flags were written) stayed `inactive`: the band silently never appeared in the one
  // place the whole design depends on it.
  useEffect(() => {
    const sync = () => setState(readImpersonationState());
    window.addEventListener("storage", sync);
    window.addEventListener(IMPERSONATION_STATE_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(IMPERSONATION_STATE_EVENT, sync);
    };
  }, []);

  // The LIVE TOKEN decides, with the flags as a fallback ONLY when no session is stored at all — the last
  // consumer still trusting the flag alone, and the safety-critical one.
  //
  // The reachable path is ordinary, not a corner case: an impersonated session carries no refresh token by
  // design, so when its 30-minute token expires the interceptor clears both tokens and navigates to `/`
  // while every `impersonation.*` key survives. The band on that login screen is INTENDED — the Return
  // button is genuinely useful there, which is why the no-session fallback exists. What must not happen is
  // the next learner signing in on that browser inheriting a non-dismissible "you are being viewed as X"
  // band over their own account, carrying a stranger's name, whose only affordance logs them out. Once a
  // real session is stored, that token's own `act` claim answers the question and the stale flags cannot.
  const hasStoredSession = Boolean(localStorage.getItem("accessToken"));
  const isImpersonating = hasStoredSession
    ? isImpersonatingBySession()
    : state.active;

  // Reserve the space only while the band is up, and give it back on unmount.
  useEffect(() => {
    const root = document.documentElement;
    if (isImpersonating) {
      root.classList.add("imb-offset");
    } else {
      root.classList.remove("imb-offset");
    }
    return () => root.classList.remove("imb-offset");
  }, [isImpersonating]);

  if (!isImpersonating) {
    return null;
  }

  const handleReturn = async () => {
    setReturning(true);
    // Best-effort audit close, and only that — it does NOT revoke the token, since nothing on this platform
    // can. A failure here must not trap the admin in the impersonated session.
    try {
      await endOwnImpersonation();
    } catch {
      // deliberately ignored; see above
    }
    handleStateLogout();
    clearImpersonationState();
    window.location.replace(retrieveAdminAppUrl() || "/");
  };

  return (
    <>
      <style>{impersonationBannerStyles}</style>
      <div className="imb-bar" role="status" aria-live="polite">
        <span className="imb-icon" aria-hidden="true">
          {/* Design-system glyph, not a bare emoji — it tints from the band's `color` like every other
              surface, which a raw ⚠ cannot. Same reason this PR moves InfoWithText off emoji. */}
          <Icon name="warning" size={16} />
        </span>
        <span className="imb-text">
          {i18n.t("impersonation_banner_text", {
            name: state.targetLabel || "",
          })}
        </span>
        {state.targetRole ? (
          <span className="imb-role">{i18n.t(state.targetRole)}</span>
        ) : null}
        <button
          type="button"
          className="imb-return"
          onClick={handleReturn}
          disabled={returning}
        >
          {returning
            ? i18n.t("impersonation_returning")
            : i18n.t("impersonation_banner_return")}
        </button>
      </div>
    </>
  );
};

export default ImpersonationBanner;
